/* eslint-disable @repo/internal/react/consistent-props-definitions */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx } from '@compiled/react';

import warnOnce from '@atlaskit/ds-lib/warn-once';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type HeaderProps } from '../Header';
import HeaderContainer from '../Header/header-container';
import { CustomItem } from '../Item/custom-item';

const styles = cssMap({
	iconContainer: {
		display: 'inline-block',
		width: '1.5rem',
		height: '1.5rem',
	},
	oldFooter: {
		userSelect: 'auto',
		display: 'block',
		textAlign: 'center',
		minHeight: '24px',
		alignItems: 'center',
		width: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-before]': {
			marginInlineEnd: token('space.0'),
			marginBlockEnd: token('space.100'),
			display: 'inline-block',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-title]': {
			textAlign: 'center',
			font: token('font.body.small'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-description]': {
			textAlign: 'center',
			display: 'inline-block',
			marginBlockStart: token('space.075'),
			marginInlineEnd: token('space.075'),
			marginBlockEnd: token('space.075'),
			marginInlineStart: token('space.075'),
		},
		// Will look interactive if the `component` is anything other than a div.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'div&:hover': {
			backgroundColor: token('color.background.neutral.subtle'),
			cursor: 'default',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'div&:active': {
			backgroundColor: token('color.background.neutral.subtle'),
			color: token('color.text.subtle'),
		},
	},
});

type NewFooterProps = Omit<HeaderProps, 'component' | 'onClick'>;

type FooterFacadeProps =
	| (HeaderProps & {
			/**
			 * @private
			 * @deprecated
			 */
			useDeprecatedApi?: true;
	  })
	| (NewFooterProps & {
			/**
			 * @private
			 * @deprecated
			 */
			useDeprecatedApi?: false;
			component?: never;
			onClick?: never;
	  });

/**
 * @deprecated `@atlaskit/side-navigation` is deprecated. Use `@atlaskit/navigation-system` instead.
 */
export type FooterProps = HeaderProps | NewFooterProps;

/**
 * __Footer__
 *
 * - [Examples](https://atlassian.design/components/side-navigation/examples#header-and-footer)
 * - [Code](https://atlassian.design/components/side-navigation/code)
 *
 * @deprecated `@atlaskit/side-navigation` is deprecated. Use `@atlaskit/navigation-system` instead.
 */
const OldFooter = (props: HeaderProps) => {
	// https://stackoverflow.com/a/39333479
	const safeProps = (({ iconBefore, onClick, description, children, testId }) => ({
		iconBefore,
		onClick,
		description,
		children,
		testId,
	}))(props);
	return (
		<CustomItem
			{...safeProps}
			component={props.component || HeaderContainer}
			css={styles.oldFooter}
		/>
	);
};

/**
 * __Footer__
 *
 * - [Examples](https://atlassian.design/components/side-navigation/examples#header-and-footer)
 * - [Code](https://atlassian.design/components/side-navigation/code)
 */
const Footer = ({ description, children, iconBefore, testId }: NewFooterProps) => {
	return (
		<Box padding="space.100" testId={testId}>
			<Stack space="space.100" alignInline="center">
				<Box xcss={styles.iconContainer}>{iconBefore}</Box>
				<Stack space="space.075">
					<Text size="small" align="center" color="inherit">
						{children}
					</Text>
					{description && (
						<Text size="small" as="p" align="center" color="color.text.subtlest">
							{description}
						</Text>
					)}
				</Stack>
			</Stack>
		</Box>
	);
};

/**
 * __Footer__
 *
 * - [Examples](https://atlassian.design/components/side-navigation/examples#header-and-footer)
 * - [Code](https://atlassian.design/components/side-navigation/code)
 *
 * @deprecated `@atlaskit/side-navigation` is deprecated. Use `@atlaskit/navigation-system` instead.
 */
export const FooterFacade: ({
	useDeprecatedApi,
	description,
	iconBefore,
	testId,
	children,
	component,
	onClick,
}: FooterFacadeProps) => JSX.Element = ({
	useDeprecatedApi = true,
	description,
	iconBefore,
	testId,
	children,
	component,
	onClick,
}: FooterFacadeProps) => {
	if (!useDeprecatedApi) {
		return (
			<Footer iconBefore={iconBefore} description={description} testId={testId}>
				{children}
			</Footer>
		);
	}

	if (
		typeof process !== 'undefined' &&
		process.env.NODE_ENV !== 'production' &&
		process.env.NODE_ENV !== 'CI'
	) {
		warnOnce(
			'The `@atlaskit/side-navigation` Footer has simplified its API, removing support for the `component` and `onClick` props.\n\nThese props will stop working in a future major release. Reach out to #help-design-system on slack for help.',
		);
	}

	return (
		<OldFooter
			onClick={onClick}
			component={component}
			iconBefore={iconBefore}
			description={description}
			testId={testId}
		>
			{children}
		</OldFooter>
	);
};

// Public API aliases preserved from an eliminated entry-point (volt-migrate-package).
export { FooterFacade as Footer };
